import React, { useState, useEffect } from 'react';
import { Settings, Facebook, MessageCircle, Save, Loader, Sparkles, CreditCard, Check, Bell, Smartphone, Calendar, ExternalLink, RefreshCw, Link2, Unlink, AlertCircle, CheckCircle2, Trash2, ChevronDown, Globe, ClipboardList, AlertTriangle, Hand, Flag, ListChecks, CheckSquare } from 'lucide-react';
import {
    initGoogleCalendarApi,
    isSignedIn,
    requestAuthorization,
    signOut as googleSignOut,
    loadGoogleScripts,
    listCalendars,
    setSelectedCalendar,
    getSelectedCalendar
} from '../../utils/googleCalendar';

import { useLanguage } from '../../contexts/LanguageContext';
import { settingsService, sourcesService, fetchWithAuth, API_BASE_URL } from '../../services/api';
import { Skeleton, Dropdown, DropdownTrigger, DropdownPopover, DropdownMenu, DropdownItem } from '../ui';
import { formatDate } from '../../utils/helpers';

// Hard-coded integration source names (must match functions/index.js)
const INTEGRATION_SOURCES = {
    meta: 'Meta Prequalification Gen'
};

export default function IntegrationsView() {
    const { t } = useLanguage();
    const [config, setConfig] = useState({

        // Browser Notifications
        browserNotificationsEnabled: true,
        // Google Calendar
        googleCalendarEnabled: false,
        googleCalendarClientId: '',
        googleCalendarApiKey: '',
        googleCalendarAutoSync: false,
        googleCalendarSyncSiteVisits: true,
        googleCalendarSyncCalls: true,
        googleCalendarSyncEvents: true,
        googleCalendarSyncBookings: true,
        googleCalendarSelectedId: 'primary'
    });
    const [googleCalendarConnected, setGoogleCalendarConnected] = useState(false);
    const [connectingGoogle, setConnectingGoogle] = useState(false);
    const [availableCalendars, setAvailableCalendars] = useState([]);
    const [loadingCalendars, setLoadingCalendars] = useState(false);
    const [saving, setSaving] = useState(false);
    const [saveSuccess, setSaveSuccess] = useState(false);
    const [saveMessage, setSaveMessage] = useState('');
    const [saveError, setSaveError] = useState('');
    const [activeTab, setActiveTab] = useState('calendar');
    const [initialLoading, setInitialLoading] = useState(true);
    const [leadSources, setLeadSources] = useState([]);



    // Auto-hide success/error message
    useEffect(() => {
        if (saveSuccess || saveError) {
            const timer = setTimeout(() => {
                setSaveSuccess(false);
                setSaveError('');
            }, 4000);
            return () => clearTimeout(timer);
        }
    }, [saveSuccess, saveError]);

    useEffect(() => {
        const loadConfig = async () => {
            setInitialLoading(true);
            try {
                const data = await settingsService.get('integrations');
                if (data) {
                    setConfig((prev) => ({ ...prev, ...data }));
                }
            } catch (error) {
                console.error('Failed to load integration settings:', error);
            } finally {
                setInitialLoading(false);
            }
        };
        loadConfig();
    }, []);

    useEffect(() => {
        const loadSources = async () => {
            try {
                const sources = await sourcesService.getAll();
                const names = (sources || []).map(d => d.name).filter(Boolean);
                const defaults = ['Meta Prequalification Gen', 'Manual Entry', 'Walk-in', 'Instagram'];
                const merged = Array.from(new Set([...names, ...defaults]));
                setLeadSources(merged);
            } catch (e) {
                console.error('Failed to load prequalification sources:', e);
                setLeadSources(['Meta Prequalification Gen', 'Manual Entry', 'Walk-in', 'Instagram']);
            }
        };
        loadSources();
    }, []);



    // Ensure integration source exists in lead_sources collection
    const ensureIntegrationSource = async (sourceName) => {
        const sourcesRef = collection(db, 'artifacts', appId, 'public', 'data', 'lead_sources');
        const snapshot = await getDocs(sourcesRef);
        const existingSources = snapshot.docs.map(d => d.data().name);

        if (!existingSources.includes(sourceName)) {
            await addDoc(sourcesRef, {
                name: sourceName,
                isIntegration: true,
                createdAt: serverTimestamp()
            });
            return true;
        }
        return false;
    };

    const handleSave = async () => {
        setSaving(true);

        const addedSources = [];



        try {
            await settingsService.update('integrations', config);

            setSaving(false);

            if (addedSources.length > 0) {
                setSaveMessage(`Integration Settings Saved!\n\nAutomatically added prequalification sources:\n• ${addedSources.join('\n• ')}`);
            } else {
                setSaveMessage('Integration Settings Saved Successfully!');
            }
            setSaveSuccess(true);
        } catch (error) {
            console.error('Failed to save settings:', error);
            setSaveError('Failed to save integration settings.');
            setSaving(false);
        }
    };

    // Check Google Calendar connection status on mount and load calendars
    useEffect(() => {
        const checkGoogleConnection = async () => {
            if (config.googleCalendarClientId && config.googleCalendarApiKey) {
                try {
                    await loadGoogleScripts();
                    const connected = isSignedIn();
                    setGoogleCalendarConnected(connected);

                    if (connected) {
                        await loadAvailableCalendars();
                        if (config.googleCalendarSelectedId) {
                            setSelectedCalendar(config.googleCalendarSelectedId);
                        }
                    }
                } catch (e) {
                    console.log('Google Calendar not connected');
                }
            }
        };
        checkGoogleConnection();
    }, [config.googleCalendarClientId, config.googleCalendarApiKey]);

    const loadAvailableCalendars = async () => {
        setLoadingCalendars(true);
        try {
            const calendars = await listCalendars();
            setAvailableCalendars(calendars);
        } catch (error) {
            console.error('Failed to load calendars:', error);
            setAvailableCalendars([]);
        }
        setLoadingCalendars(false);
    };

    const handleGoogleConnect = async () => {
        if (!config.googleCalendarClientId || !config.googleCalendarApiKey) {
            setSaveError('Please enter your Google Calendar Client ID and API Key first, then save.');
            return;
        }

        setConnectingGoogle(true);
        try {
            await initGoogleCalendarApi(config.googleCalendarClientId, config.googleCalendarApiKey);
            await requestAuthorization();
            setGoogleCalendarConnected(true);
            await loadAvailableCalendars();
            setSaveMessage('Successfully connected to Google Calendar!');
            setSaveSuccess(true);
        } catch (error) {
            console.error('Google Calendar connection failed:', error);
            setSaveError('Failed to connect to Google Calendar. Please check your credentials.');
        }
        setConnectingGoogle(false);
    };

    const handleGoogleDisconnect = () => {
        googleSignOut();
        setAvailableCalendars([]);
        setGoogleCalendarConnected(false);
        setSaveMessage('Disconnected from Google Calendar.');
        setSaveSuccess(true);
    };

    const tabs = [
        { id: 'calendar', label: t('calendar'), icon: Calendar }
    ];

    return (
        <div className="space-y-4 animate-fade-in">
            {/* Header */}
            <div className="mb-4">
                <h2 className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-indigo-600">
                    {t('integrationsHub')}
                </h2>
                <p className="text-gray-500 mt-1">{t('connectExternalPlatforms')}</p>
            </div>

            {/* Card wrapper */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 flex flex-col overflow-hidden relative">

                {/* Error Toast */}
                {saveError && (
                    <div className="absolute top-4 right-4 z-[9999] flex items-center gap-3 px-4 py-3 rounded-xl shadow-lg text-sm font-medium bg-red-600 text-white animate-fade-in">
                        <span className="flex items-center gap-1"><AlertTriangle className="w-4 h-4" /> {saveError}</span>
                        <button onClick={() => setSaveError('')} className="ml-2 opacity-70 hover:opacity-100">✕</button>
                    </div>
                )}

                {/* Success Modal Overlay */}
                {saveSuccess && (
                    <div className="absolute inset-0 z-50 flex items-center justify-center p-4 bg-white/80 backdrop-blur-sm animate-in fade-in duration-200">
                        <div className="bg-white border rounded-2xl shadow-2xl p-8 max-w-sm w-full text-center transform animate-in zoom-in-95 duration-200">
                            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-green-100 mb-6">
                                <Check className="h-8 w-8 text-green-600" />
                            </div>
                            <h3 className="text-xl font-bold text-gray-900 mb-2">{t('success')}!</h3>
                            <p className="text-gray-600 whitespace-pre-line leading-relaxed">
                                {saveMessage}
                            </p>
                            <button
                                onClick={() => setSaveSuccess(false)}
                                className="mt-8 w-full inline-flex justify-center rounded-xl bg-blue-600 px-4 py-3 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 transition-colors"
                            >
                                Continue
                            </button>
                        </div>
                    </div>
                )}

                {/* Tabs */}
                <div className="flex border-b bg-gray-50 overflow-x-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
                    {tabs.map((tab) => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`flex-1 min-w-max px-4 py-3 text-sm font-medium flex items-center justify-center gap-2 border-b-2 transition-colors ${activeTab === tab.id
                                ? 'border-blue-600 text-blue-600 bg-white'
                                : 'border-transparent text-gray-500 hover:text-gray-700'
                                }`}
                        >
                            <tab.icon className="w-4 h-4" />
                            {tab.label}
                        </button>
                    ))}
                </div>

                <div className="p-6 flex-1 overflow-y-auto bg-slate-50">
                    {initialLoading ? (
                        <div className="space-y-6">
                            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                                <div className="p-5 border-b bg-gray-50 flex items-center gap-3">
                                    <Skeleton variant="circular" width={32} height={32} animation="wave" />
                                    <div>
                                        <Skeleton width={200} height={20} animation="wave" className="mb-1" />
                                        <Skeleton width={150} height={16} animation="wave" />
                                    </div>
                                </div>
                                <div className="p-5 space-y-4">
                                    {[1, 2].map(i => (
                                        <div key={i}>
                                            <Skeleton width={120} height={16} animation="wave" className="mb-2 uppercase" />
                                            <Skeleton width="100%" height={45} animation="wave" className="rounded-lg" />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    ) : (
                        <>





                    {/* Google Calendar */}
                    {activeTab === 'calendar' && (
                        <div className="space-y-6">
                            <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
                                <div className="flex items-center gap-3 mb-4 border-b border-gray-100 pb-4">
                                    <div className="p-2 bg-blue-100 text-blue-600 rounded-lg">
                                        <Calendar className="w-6 h-6" />
                                    </div>
                                    <div className="flex-1">
                                        <h3 className="font-bold text-gray-900">{t('googleCalendarIntegration')}</h3>
                                        <p className="text-xs text-gray-500">{t('googleCalendarDesc')}</p>
                                    </div>
                                    <label className="relative inline-flex items-center cursor-pointer">
                                        <input
                                            type="checkbox"
                                            className="sr-only peer"
                                            checked={config.googleCalendarEnabled}
                                            onChange={(e) =>
                                                setConfig({ ...config, googleCalendarEnabled: e.target.checked })
                                            }
                                        />
                                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                                    </label>
                                </div>

                                {config.googleCalendarEnabled && (
                                    <div className="space-y-4">
                                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                                            <p className="text-xs text-blue-700">
                                                <strong>Quick Add:</strong> {t('quickAddDesc')}
                                            </p>
                                        </div>

                                        <div className="border-t border-gray-100 pt-4 mt-4">
                                            <div className="flex items-center gap-2 mb-3">
                                                <RefreshCw className="w-4 h-4 text-gray-500" />
                                                <p className="text-xs font-bold text-gray-500 uppercase">
                                                    {t('autoSync')}
                                                </p>
                                            </div>

                                            <div className="space-y-3">
                                                <div>
                                                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">
                                                        {t('googleCloudClientId')}
                                                    </label>
                                                    <input
                                                        type="password"
                                                        className="w-full p-3 border rounded-lg text-sm outline-none font-mono focus:border-blue-400"
                                                        value={config.googleCalendarClientId}
                                                        onChange={(e) =>
                                                            setConfig({ ...config, googleCalendarClientId: e.target.value })
                                                        }
                                                        placeholder="xxxx.apps.googleusercontent.com"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">
                                                        {t('googleCalendarApiKey')}
                                                    </label>
                                                    <input
                                                        type="password"
                                                        className="w-full p-3 border rounded-lg text-sm outline-none font-mono focus:border-blue-400"
                                                        value={config.googleCalendarApiKey}
                                                        onChange={(e) =>
                                                            setConfig({ ...config, googleCalendarApiKey: e.target.value })
                                                        }
                                                        placeholder="AIzaSy..."
                                                    />
                                                    <p className="text-xs text-gray-400 mt-1">
                                                        {t('getFrom') || 'Get from'}{' '}
                                                        <a
                                                            href="https://console.cloud.google.com/apis/credentials"
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="text-blue-500 hover:underline inline-flex items-center gap-1"
                                                        >
                                                            {t('googleCloudConsole') || 'Google Cloud Console'} <ExternalLink className="w-3 h-3" />
                                                        </a>
                                                    </p>
                                                </div>

                                                {/* Connect/Disconnect Button */}
                                                <div className="flex items-center gap-3">
                                                    {googleCalendarConnected ? (
                                                        <>
                                                            <span className="flex items-center gap-2 text-sm text-green-600">
                                                                <Check className="w-4 h-4" /> Connected
                                                            </span>
                                                            <button
                                                                type="button"
                                                                onClick={handleGoogleDisconnect}
                                                                className="px-3 py-1.5 text-xs bg-red-50 text-red-700 rounded-lg hover:bg-red-100 transition-colors"
                                                            >
                                                                {t('disconnect') || 'Disconnect'}
                                                            </button>
                                                        </>
                                                    ) : (
                                                        <button
                                                            type="button"
                                                            onClick={handleGoogleConnect}
                                                            disabled={connectingGoogle || !config.googleCalendarClientId || !config.googleCalendarApiKey}
                                                            className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                                                        >
                                                            {connectingGoogle ? (
                                                                <>
                                                                    <Loader className="w-4 h-4 animate-spin" /> {t('connectingGoogle') || 'Connecting...'}
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <Calendar className="w-4 h-4" /> {t('connectGoogleCalendar') || 'Connect Google Calendar'}
                                                                </>
                                                            )}
                                                        </button>
                                                    )}
                                                </div>

                                                {/* Calendar Selector */}
                                                {googleCalendarConnected && (
                                                    <div className="border-t border-gray-100 pt-4 mt-4">
                                                        <label className="block text-xs font-bold text-gray-500 uppercase mb-2">
                                                            {t('selectCalendar') || 'Select Calendar'}
                                                        </label>
                                                        {loadingCalendars ? (
                                                            <div className="flex items-center gap-2 text-sm text-gray-500">
                                                                <Loader className="w-4 h-4 animate-spin" /> {t('loadingCalendars') || 'Loading calendars...'}
                                                            </div>
                                                        ) : (
                                                            <>
                                                                <Dropdown>
                                                                    <DropdownTrigger>
                                                                        <button className="w-full p-3 border rounded-lg text-sm outline-none focus:border-blue-400 flex justify-between items-center text-left bg-white">
                                                                            {availableCalendars.length === 0 ? (t('primaryCalendar') || 'Primary Calendar') : 
                                                                             (availableCalendars.find(c => c.id === (config.googleCalendarSelectedId || 'primary'))?.name + (availableCalendars.find(c => c.id === (config.googleCalendarSelectedId || 'primary'))?.primary ? ' (Primary)' : '') || (t('primaryCalendar') || 'Primary Calendar'))}
                                                                            <ChevronDown className="w-4 h-4 text-gray-400 shrink-0 ml-2" />
                                                                        </button>
                                                                    </DropdownTrigger>
                                                                    <DropdownPopover className="w-full max-h-60 overflow-y-auto">
                                                                        <DropdownMenu
                                                                            selectedKeys={new Set([config.googleCalendarSelectedId || 'primary'])}
                                                                            selectionMode="single"
                                                                            onSelectionChange={(keys) => {
                                                                                const val = Array.from(keys)[0];
                                                                                setConfig({ ...config, googleCalendarSelectedId: val });
                                                                                setSelectedCalendar(val);
                                                                            }}
                                                                        >
                                                                            {availableCalendars.length === 0 ? (
                                                                                <DropdownItem id="primary">{t('primaryCalendar') || 'Primary Calendar'}</DropdownItem>
                                                                            ) : (
                                                                                availableCalendars.map((cal) => (
                                                                                    <DropdownItem key={cal.id} id={cal.id}>
                                                                                        {cal.name} {cal.primary ? '(Primary)' : ''}
                                                                                    </DropdownItem>
                                                                                ))
                                                                            )}
                                                                        </DropdownMenu>
                                                                    </DropdownPopover>
                                                                </Dropdown>
                                                                <p className="text-xs text-gray-400 mt-1">
                                                                    {t('allEventsSyncedToCalendar') || 'All CRM events will be synced to this calendar'}
                                                                </p>
                                                                {availableCalendars.length === 0 && (
                                                                    <button
                                                                        type="button"
                                                                        onClick={loadAvailableCalendars}
                                                                        className="mt-2 text-xs text-blue-600 hover:underline flex items-center gap-1"
                                                                    >
                                                                        <RefreshCw className="w-3 h-3" /> {t('refreshCalendarList') || 'Refresh calendar list'}
                                                                    </button>
                                                                )}
                                                            </>
                                                        )}
                                                    </div>
                                                )}

                                                {/* Auto Sync Toggle */}
                                                {googleCalendarConnected && (
                                                    <div className="border-t border-gray-100 pt-4 mt-4">
                                                        <div className="flex items-center justify-between mb-3">
                                                            <div>
                                                                <p className="text-sm font-medium text-gray-900">{t('autoSyncEventsTitle') || 'Auto-Sync Events'}</p>
                                                                <p className="text-xs text-gray-500">{t('autoSyncEventsDesc') || 'Automatically create/update calendar events'}</p>
                                                            </div>
                                                            <label className="relative inline-flex items-center cursor-pointer">
                                                                <input
                                                                    type="checkbox"
                                                                    className="sr-only peer"
                                                                    checked={config.googleCalendarAutoSync}
                                                                    onChange={(e) =>
                                                                        setConfig({ ...config, googleCalendarAutoSync: e.target.checked })
                                                                    }
                                                                />
                                                                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                                                            </label>
                                                        </div>

                                                        {config.googleCalendarAutoSync && (
                                                            <div className="space-y-2 ml-1">
                                                                <p className="text-xs font-bold text-gray-500 uppercase mb-2">
                                                                    {t('syncTheseEventsLabel') || 'Sync These Events:'}
                                                                </p>
                                                                <label className="flex items-center gap-3 cursor-pointer">
                                                                    <input
                                                                        type="checkbox"
                                                                        className="w-4 h-4 text-blue-600 rounded"
                                                                        checked={config.googleCalendarSyncSiteVisits}
                                                                        onChange={(e) =>
                                                                            setConfig({ ...config, googleCalendarSyncSiteVisits: e.target.checked })
                                                                        }
                                                                    />
                                                                    <span className="text-sm text-gray-700">{t('siteVisitsLabel') || 'Site Visits'}</span>
                                                                </label>
                                                                <label className="flex items-center gap-3 cursor-pointer">
                                                                    <input
                                                                        type="checkbox"
                                                                        className="w-4 h-4 text-blue-600 rounded"
                                                                        checked={config.googleCalendarSyncCalls}
                                                                        onChange={(e) =>
                                                                            setConfig({ ...config, googleCalendarSyncCalls: e.target.checked })
                                                                        }
                                                                    />
                                                                    <span className="text-sm text-gray-700">{t('scheduledCallsLabel') || 'Scheduled Calls'}</span>
                                                                </label>
                                                                <label className="flex items-center gap-3 cursor-pointer">
                                                                    <input
                                                                        type="checkbox"
                                                                        className="w-4 h-4 text-blue-600 rounded"
                                                                        checked={config.googleCalendarSyncEvents}
                                                                        onChange={(e) =>
                                                                            setConfig({ ...config, googleCalendarSyncEvents: e.target.checked })
                                                                        }
                                                                    />
                                                                    <span className="text-sm text-gray-700">{t('eventDatesLabel') || 'Event Dates'}</span>
                                                                </label>
                                                                <label className="flex items-center gap-3 cursor-pointer">
                                                                    <input
                                                                        type="checkbox"
                                                                        className="w-4 h-4 text-blue-600 rounded"
                                                                        checked={config.googleCalendarSyncBookings}
                                                                        onChange={(e) =>
                                                                            setConfig({ ...config, googleCalendarSyncBookings: e.target.checked })
                                                                        }
                                                                    />
                                                                    <span className="text-sm text-gray-700">{t('finalBookingsLabel') || 'Final Bookings'}</span>
                                                                </label>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                                <h4 className="font-bold text-blue-800 mb-2">{t('howItWorks')}:</h4>
                                <ul className="text-sm text-blue-700 space-y-1">
                                    <li className="flex items-start gap-2"><Check className="w-4 h-4 mt-0.5 shrink-0" /> <span><strong>{t('quickAdd')}</strong> - {t('calBenefit1') || 'Add any date to calendar with one click'}</span></li>
                                    <li className="flex items-start gap-2"><Check className="w-4 h-4 mt-0.5 shrink-0" /> <span><strong>{t('siteVisitsLabel') || 'Site Visits'}</strong> - {t('calBenefit2') || 'Schedule venue visits with client details'}</span></li>
                                    <li className="flex items-start gap-2"><Check className="w-4 h-4 mt-0.5 shrink-0" /> <span><strong>{t('scheduledCallsLabel') || 'Scheduled Calls'}</strong> - {t('calBenefit3') || 'Never miss a follow-up call'}</span></li>
                                    <li className="flex items-start gap-2"><Check className="w-4 h-4 mt-0.5 shrink-0" /> <span><strong>{t('eventDatesLabel') || 'Event Dates'}</strong> - {t('calBenefit4') || 'Track client event dates'}</span></li>
                                    <li className="flex items-start gap-2"><Check className="w-4 h-4 mt-0.5 shrink-0" /> <span><strong>{t('finalBookingsLabel') || 'Final Bookings'}</strong> - {t('calBenefit5') || 'Confirmed booking dates'}</span></li>
                                </ul>
                                <p className="text-xs text-blue-600 mt-3">
                                    <strong>{t('tipLabel') || 'Tip:'}</strong> {t('calTip') || 'Quick Add works without any API setup. Auto-sync requires Google Cloud credentials.'}
                                </p>
                            </div>
                        </div>
                    )}
                    </>
                    )}
                </div>

                {/* Save Button */}
                {activeTab !== 'facebook' && (
                    <div className="p-4 bg-white border-t border-gray-200 flex justify-end">
                        <button
                            onClick={handleSave}
                            disabled={saving}
                            className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl flex items-center gap-2 disabled:opacity-70"
                        >
                            {saving ? <Loader className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}{' '}
                            {t('save')}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}
